// @ts-ignore
// @ts-nocheck
import React, { useEffect, useMemo } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import JWCarouselSlide from './JWCarouselSlide';
import { JWCarouselSlideProps } from './JWCarouselSlide';
import PlusIcon from '../../icons/PlusIcon';
import { ArrowRightIcon, ArrowLeftIcon } from '../../icons';
import { useCarousel } from '../../hooks/useCarousel';
import { getHighlightCarouselSlideKey } from '@/utils/highlightCarouselKeys';
import 'swiper/css';
import 'swiper/css/pagination';
import SpotifyIcon from '../../icons/SpotifyIcon';
import YoutubeIcon from '../../icons/YoutubeIcon';
import SoundcloudIconSVG from '../../icons/SoundcloudIconSVG';
import { PlaceholderLabel } from '../../Profile/PlaceholderLabel';

export interface JWCarouselProps {
    id: string
    addHighlightsClick?: () => void
    children?: React.ReactNode
    slides?: Array<JWCarouselSlideProps> | null
    onSlideClick?: (item: JWCarouselSlideProps, index: number) => void
    isLoading: boolean
    canEdit?: boolean
    slidesPerView?: number
    slidesPerGroup?: number
}

function JWCarousel(props: JWCarouselProps) {
    const {
        swiperComponent,
        showRightArrow,
        showLeftArrow,
        noSlides,
        isMobile,
        setSwiperComponent,
        slideNext,
        slidePrev,
        onSlideChange,
        onFromEdge,
        effectiveSlidesPerView,
        effectiveSlidesPerGroup
    } = useCarousel({
        slides: props.slides,
        initialSlidesPerView: props.slidesPerView || 3.5,
        initialSlidesPerGroup: props.slidesPerGroup || 2
    });

    const slidesIdentityKey = useMemo(
        () =>
            props.slides?.map((item, i) => getHighlightCarouselSlideKey(item, i)).join('|') ??
            '',
        [props.slides]
    );

    useEffect(() => {
        if (!swiperComponent || !props.slides?.length) return;
        swiperComponent.update();
    }, [swiperComponent, slidesIdentityKey, props.slides?.length]);

    const isAtMaxCapacity = props.slides && props.slides.length >= 30;

    return (
        <div className={`w-full h-full relative jw-carousel-container ${props.slides && props.slides.length <= 3 ? 'few-slides' : ''}`}>
            {/* Left Arrow Button */}
            {showLeftArrow && (
                <button
                    onClick={slidePrev}
                    className={`scroll-background absolute z-10 h-full top-0 left-0 rounded-l-xl cursor-pointer ${isMobile ? 'left-arrow-mobile' : 'w-12'}`}
                >
                    <div className={`absolute ${isMobile ? 'top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2' : 'top-[40%] right-[33%]'}`}>
                        <ArrowLeftIcon id={props.id} />
                    </div>
                </button>
            )}

            {noSlides && props.canEdit ? (
                <div className="flex flex-col items-center justify-center h-full w-full text-purple">
                    {!isAtMaxCapacity ? (
                        <button
                            onClick={props?.addHighlightsClick}
                            className="group flex-col items-center justify-center space-y-3 w-full h-full bg-blue-200 rounded-lg pt-2 pb-2 pl-2 flex-grow transition-colors hover:bg-blue-300"
                            aria-label="Add a highlight. Show us what you can do"
                            style={{
                                paddingRight: '2px',
                                position: 'relative',
                                overflow: 'hidden',
                                minHeight: '150px',
                                maxHeight: 'none'
                            }}
                        >
                            <div className="flex flex-col items-center justify-center">
                                <PlusIcon />
                                <p><PlaceholderLabel title="Add a highlight" benefit="Show us what you can do" /></p>
                            </div>
                            <div className="flex items-center justify-center mt-3 space-x-3">
                                <SpotifyIcon />
                                <span className="text-gray-400">|</span>
                                <SoundcloudIconSVG />
                                <span className="text-gray-400">|</span>
                                <YoutubeIcon />
                            </div>
                        </button>
                    ) : (
                        <div className="flex flex-col justify-center items-center w-full h-full bg-red-100 rounded p-4 text-center">
                            <p className="text-red-600 font-medium">Maximum limit of 30 highlights reached</p>
                            <p className="text-red-600 text-sm">Delete some highlights before adding more</p>
                        </div>
                    )}
                </div>
            ) : (
                <Swiper
                    spaceBetween={10}
                    slidesPerView={effectiveSlidesPerView}
                    slidesPerGroup={effectiveSlidesPerGroup}
                    onSlideChange={onSlideChange}
                    onFromEdge={onFromEdge}
                    onSwiper={setSwiperComponent}
                    modules={[Pagination]}
                    className={`h-full ${isMobile ? 'px-2' : ''}`}
                    centeredSlides={isMobile || props.slidesPerView === 1}
                    preventInteractionOnTransition={true}
                    touchRatio={1}
                    touchAngle={45}
                    touchMoveStopPropagation={true}
                >
                    {/* SwiperSlides have to be direct children of the Swiper component */}
                    {props?.slides?.map((item, index) => (
                        <SwiperSlide
                            key={getHighlightCarouselSlideKey(item, index)}
                            className="flex bg-transparent h-full p-2 justify-center"
                        >
                            <JWCarouselSlide
                                url={item?.url}
                                image={item?.image}
                                description={item?.description}
                                coverArtUrl={item?.coverArtUrl}
                                mediaUrl={item?.mediaUrl}
                                typeCd={item?.typeCd}
                                jamBoardPinColor={item?.jamBoardPinColor}
                                jamBoardRateLabel={item?.jamBoardRateLabel}
                                onSlideClick={() => props?.onSlideClick?.(item, index)}
                            />
                        </SwiperSlide>
                    ))}
                </Swiper>
            )}

            {/* Right Arrow Button */}
            {showRightArrow && (
                <button
                    onClick={slideNext}
                    className={`scroll-background absolute z-10 h-full top-0 right-0 rounded-r-xl cursor-pointer ${isMobile ? 'right-arrow-mobile' : 'w-12'}`}
                >
                    <div className={`absolute ${isMobile ? 'top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2' : 'top-[40%] right-[33%]'}`}>
                        <ArrowRightIcon id={props.id} />
                    </div>
                </button>
            )}
        </div>
    );
}

export default JWCarousel;
